/** 'Model' as in 'type of object' */
export type CacheableModel = "root" | "database" | "dashboard" | "question";

export type CacheStrategyType =
  | "nocache"
  | "ttl" // aka Adaptive
  | "duration"
  | "schedule"
  | "inherit";

interface CacheStrategyBase {
  type: CacheStrategyType;
}

export enum CacheDurationUnit {
  Hours = "hours",
  Minutes = "minutes",
  Seconds = "seconds",
  Days = "days",
}

export interface AdaptiveStrategy extends CacheStrategyBase {
  type: "ttl";
  multiplier: number;
  min_duration_ms: number;
  min_duration_seconds?: number;
}

export interface DoNotCacheStrategy extends CacheStrategyBase {
  type: "nocache";
}

export interface DurationStrategy extends CacheStrategyBase {
  type: "duration";
  duration: number;
  unit: CacheDurationUnit;
  refresh_automatically: boolean;
}

export interface InheritStrategy extends CacheStrategyBase {
  type: "inherit";
}

export interface ScheduleStrategy extends CacheStrategyBase {
  type: "schedule";
  schedule: string;
  refresh_automatically: boolean;
}

/** Cache invalidation strategy */
export type CacheStrategy =
  | DoNotCacheStrategy
  | AdaptiveStrategy
  | DurationStrategy
  | InheritStrategy
  | ScheduleStrategy;

export interface CacheRefreshStatus {
  status: "running" | "success" | "failed";
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  query_count: number | null;
  error: string | null;
}

/** Cache invalidation configuration */
export interface CacheConfig {
  /** The type of cacheable object this configuration concerns */
  model: CacheableModel;
  model_id: number;
  next_run_at?: string | null;
  invalidated_at?: string | null;
  refresh_status?: CacheRefreshStatus | null;
  /** Cache invalidation strategy */
  strategy: CacheStrategy;
}

export type CacheConfigAPIResponse = {
  data: CacheConfig[];
};

export interface CacheConfigSummary {
  configured_dashboards: number;
  scheduled_dashboards: number;
  auto_refresh_dashboards: number;
  configured_questions: number;
  auto_refresh_questions: number;
  due_items: number;
  running_items: number;
  failed_items: number;
}

export type PreemptiveCacheableModel = "dashboard" | "question";

export interface CacheRefreshRunCardDetails {
  card_id: number;
  dashboard_id: number | null;
  query_count: number;
  success_count: number;
  failed_count: number;
  errors: string[];
}

export interface CacheRefreshRunDetails {
  planned_query_count: number;
  completed_query_count: number;
  successful_query_count: number;
  failed_query_count: number;
  trigger?: "automatic" | "manual";
  dashboard_tab_count: number;
  refreshed_tab_count: number;
  dashboard_card_count: number;
  refreshed_card_count: number;
  cards: CacheRefreshRunCardDetails[];
}

export interface CacheRefreshNowResponse {
  status: "started" | "running" | "skipped";
  reason?: string | null;
  query_count?: number;
}

/** One row of the preemptive cache refresh run history */
export interface CacheRefreshRun {
  id: number;
  model: PreemptiveCacheableModel;
  model_id: number;
  status: "running" | "success" | "failed";
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  query_count: number | null;
  error: string | null;
  created_at: string;
  item_name: string | null;
  details: CacheRefreshRunDetails | null;
}

/** One row of the cache configuration change history */
export interface CacheConfigChange {
  id: number;
  cache_config_id: number | null;
  model: PreemptiveCacheableModel;
  model_id: number;
  action: "created" | "updated" | "deleted";
  strategy: string | null;
  before_config: Record<string, unknown> | null;
  after_config: Record<string, unknown> | null;
  user_id: number | null;
  created_at: string;
  item_name: string | null;
  user_first_name: string | null;
  user_last_name: string | null;
  user_email: string | null;
}

export interface PaginatedList<T> {
  data: T[];
  total: number;
}
